import type { View } from '@seen/core/models/view'
import { Button, TypographySubtle } from '@seen/ui'
import { IconBuilding, IconEye } from '@tabler/icons-react'
import type * as React from 'react'
import { z } from 'zod'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'

export function ViewComposerDialogContent({
  onSubmit,
  isSubmitting,
  view,
}: {
  onSubmit: ({
    name,
    access,
  }: {
    name: View['name']
    access: View['access']
  }) => any
  isSubmitting?: boolean
  view?: View
}): React.ReactNode {
  const currentPractitioner = useCurrentPractitioner()

  const form = useForm({
    schema: z.object({
      name: z.string().min(1),
      visibility: z.enum(['private', 'shared']),
    }),
    defaultValues: {
      name: view?.name ?? '',
      visibility: view?.access === 'shared' ? 'shared' : 'private',
    },
  })

  const submit = form.handleSubmit((values) => {
    onSubmit({
      name: values.name,
      access:
        values.visibility === 'shared'
          ? 'shared'
          : currentPractitioner.reference,
    })
  })

  return (
    <Form onSubmit={submit}>
      <DialogHeader className="flex flex-row-reverse items-center py-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <RichTextEditor
              placeholder="View name"
              variant="unstyled"
              className="flex-1"
              contentClassName="text-xl font-bold"
              {...field}
            />
          )}
        />
      </DialogHeader>
      <DialogFooter>
        <FormField
          control={form.control}
          name="visibility"
          render={({ field: { value, onChange, ...rest } }) => (
            <div className="flex items-center gap-2">
              <TypographySubtle className="text-foreground-secondary">
                Save to
              </TypographySubtle>
              <Combobox
                options={[
                  {
                    label: (
                      <ComboboxOption isSelected={value === 'private'}>
                        <IconEye className="size-4 shrink-0" />
                        Private
                      </ComboboxOption>
                    ),
                    value: 'private',
                    keywords: ['Private'],
                  },
                  {
                    label: (
                      <ComboboxOption isSelected={value === 'shared'}>
                        <IconBuilding className="size-4 shrink-0" />
                        Organization
                      </ComboboxOption>
                    ),
                    value: 'shared',
                    keywords: ['Shared'],
                  },
                ]}
                onSelect={onChange}
              >
                <Button variant="outline" className="gap-2" {...rest}>
                  {value === 'private' && (
                    <>
                      <IconEye className="size-4 shrink-0" />
                      Private
                    </>
                  )}
                  {value === 'shared' && (
                    <>
                      <IconBuilding className="size-4 shrink-0" />
                      Organization
                    </>
                  )}
                </Button>
              </Combobox>
            </div>
          )}
        />
        <div className="my-1 w-[0.5px] self-stretch bg-border" />
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <SubmitButton submitting={isSubmitting}>
          {view ? 'Update view' : 'Create view'}
        </SubmitButton>
      </DialogFooter>
    </Form>
  )
}
